import React, { useState, useEffect, useMemo } from "react";
import { useFormik } from "formik";
import * as Components from "../../../components";
import {
  useCreateRoleMutation,
  useGetRoleQuery,
  useUpdateRoleMutation,
  useGetAppFeaturesQuery,
  useGetAppsQuery,
  useGetPluginsQuery,
} from "../services";
import { rolesFormValidation } from "../validations";
import { mergeObjects } from "../../../shared/helper";
import { FormContainer } from "../Shared";
import { useForm } from "../../../hooks";
import { getLocalizedString } from "../../../shared/translation";
import { categorizeFeatures } from "./roleFeatureUtils";
import { PLUGIN_SEPARATOR } from "../Customization/PluginsManager/pluginFeatureUtils";

const EMPTY_PLUGINS_DATA = { data: [] };
const EMPTY_APP_FEATURES = {};
const EMPTY_APPS = {};

const SELECTED_APP_ID_KEY = "selected-app-id";
const DEFAULT_APP_ID = "1";

/**
 * Read the currently selected app id from localStorage. Falls back to "1" when the
 * key is missing, empty, or localStorage is unavailable (e.g. SSR, private mode).
 */
const getSelectedAppId = () => {
  try {
    const stored = typeof window !== "undefined" && window.localStorage
      ? window.localStorage.getItem(SELECTED_APP_ID_KEY)
      : null;
    return stored && String(stored).trim() ? String(stored) : DEFAULT_APP_ID;
  } catch {
    return DEFAULT_APP_ID;
  }
};

export const RoleForm = ({ idToEdit = null, onClose }) => {
  const [initialValues, setInitialValues] = useState({
    name: "",
    // Source the app id from localStorage (the selector stored elsewhere). If the
    // key is missing or localStorage is unavailable, fall back to "1" so validation
    // passes. Edit mode uses the same fallback when the API omits app_id.
    app_id: getSelectedAppId(),
    enabled_app_feature_ids: [],
    plugin_feature_permissions: [],
  });

  const formik = useFormik({
    initialValues,
    validationSchema: rolesFormValidation,
    enableReinitialize: true,
    onSubmit: (values) => handleFormSubmit(values),
  });

  const [actionIds, setActionIds] = useState({ create: [], delete: [], update: [] });

  const { create, update } = useForm({
    createMutation: useCreateRoleMutation,
    updateMutation: useUpdateRoleMutation,
    closeForm: onClose,
    setError: formik.setErrors,
  });

  // Get existing role data if form is in edit mode
  const { data } = useGetRoleQuery({ id: idToEdit }, { skip: !idToEdit });

  const { data: apps = EMPTY_APPS } = useGetAppsQuery({ page: 1, per_page: 1000 });

  const { data: appFeatures = EMPTY_APP_FEATURES, isLoading: isAppFeaturesLoading } = useGetAppFeaturesQuery({
    app_id: 1,
    per_page: 1000,
    page: 0,
    order_by: "name",
    order_dir: "asc",
  });

  // Fetch registered plugins so we can show each plugin individually
  const { data: pluginsData = EMPTY_PLUGINS_DATA } = useGetPluginsQuery({
    page: 1,
    per_page: 1000,
    order_by: "plugins.created_at",
    order_dir: "asc",
  });

  // Normalize `appFeatures` to a map keyed by feature name so component
  // works whether the API returns an array (wrapped in `data`) or an
  // already-shaped object used elsewhere in the app.
  const normalizedAppFeatures = useMemo(() => {
    const result = {};
    const featuresArray = Array.isArray(appFeatures?.data)
      ? appFeatures.data
      : Array.isArray(appFeatures)
      ? appFeatures
      : null;

    if (featuresArray) {
      featuresArray.forEach((feat) => {
        const featureName = feat.name || feat.label_name || "unknown";
        if (!result[featureName]) result[featureName] = {};
        // place id under its label (create/update/delete/read)
        if (feat.label_name) result[featureName][feat.label_name] = feat.id;
      });
      return result;
    }

    // If already an object mapping, return as-is
    return appFeatures || {};
  }, [appFeatures]);

  // Categorize features into regular and plugin groups
  const { regularFeatures, pluginGroups } = useMemo(
    () => categorizeFeatures(normalizedAppFeatures),
    [normalizedAppFeatures]
  );

  // Identify plugins that don't have sub-features registered in app_features.
  // These will be shown as individual rows using the generic "Plugins" permission.
  const pluginsWithoutFeatures = useMemo(() => {
    if (!pluginsData?.data?.length) return [];
    return pluginsData.data.filter(
      (plugin) => plugin.is_enabled && !pluginGroups[plugin.name]
    );
  }, [pluginsData, pluginGroups]);

  // Build a unified list of all enabled plugins for rendering under "Plugins" row.
  // Each entry knows whether it has sub-features or not.
  // Sub-features can come from two sources:
  //   1. app_features API (pluginGroups) — features registered as "PluginName::FeatureKey"
  //   2. plugin_features API (features_json.config) — features declared in the plugin bundle
  // Source 1 takes priority. Source 2 is used as fallback for plugins that have
  // plugin_features but no corresponding app_features entries yet.
  //
  // Plugins that have neither sub-features nor a plugin_features record cannot be tracked
  // individually (no plugin_feature_id to send to the backend), so they are excluded from
  // the list entirely. Their access is implicitly controlled by the parent "Plugins" row.
  const allPluginEntries = useMemo(() => {
    if (!pluginsData?.data?.length) return [];
    return pluginsData.data
      .filter((plugin) => plugin.is_enabled)
      .map((plugin) => {
        const hasAppFeatureSubs = !!pluginGroups[plugin.name];
        const pluginFeatConfig = plugin.plugin_features?.[0]?.features_json?.config;
        const hasPluginFeatSubs = Array.isArray(pluginFeatConfig) && pluginFeatConfig.length > 0;
        const hasPluginFeatureRecord = plugin.plugin_features?.length > 0;

        return {
          ...plugin,
          hasSubFeatures: hasAppFeatureSubs || hasPluginFeatSubs,
          subFeatures: pluginGroups[plugin.name] || null,
          // Sub-features from plugin_features API (only used when no app_features entries)
          pluginFeatConfig: !hasAppFeatureSubs && hasPluginFeatSubs ? pluginFeatConfig : null,
          hasPluginFeatureRecord,
        };
      })
      // Exclude plugins that have no sub-features AND no plugin_features record —
      // these cannot be tracked individually and would be misleading to show.
      .filter((plugin) => plugin.hasSubFeatures || plugin.hasPluginFeatureRecord);
  }, [pluginsData, pluginGroups]);

  // Build a map: pluginName → plugin_feature_id (from plugin_features[0].id)
  // This is used to construct plugin_feature_permissions payload.
  const pluginFeatureIdMap = useMemo(() => {
    const map = {};
    if (!pluginsData?.data?.length) return map;
    pluginsData.data.forEach((plugin) => {
      if (plugin.is_enabled && plugin.plugin_features?.length > 0) {
        map[plugin.name] = plugin.plugin_features[0].id;
      }
    });
    return map;
  }, [pluginsData]);

  // Collect all app_feature IDs that belong to plugin sub-features (PluginName::FeatureKey).
  // These IDs should NOT be sent in enabled_app_feature_ids but instead in plugin_feature_permissions.
  const pluginSubFeatureIds = useMemo(() => {
    const ids = new Set();
    Object.keys(pluginGroups).forEach((pluginName) => {
      const features = pluginGroups[pluginName];
      Object.keys(features).forEach((featureKey) => {
        const fullName = `${pluginName}${PLUGIN_SEPARATOR}${featureKey}`;
        const feat = normalizedAppFeatures[fullName];
        if (feat) {
          ["read", "create", "update", "delete"].forEach((action) => {
            if (feat[action]) ids.add(feat[action]);
          });
        }
      });
    });
    return ids;
  }, [pluginGroups, normalizedAppFeatures]);

  // Track which plugin groups are expanded in the UI
  const [expandedPlugins, setExpandedPlugins] = useState({});

  const togglePluginGroup = (pluginName) => {
    setExpandedPlugins((prev) => ({ ...prev, [pluginName]: !prev[pluginName] }));
  };

  /**
   * The parent "Plugins" Read checkbox is locked (disabled) when any of the parent's
   * Create/Update/Delete permissions is enabled, because Read is auto-required by them.
   * When Read is locked, child→parent cascade must NOT toggle Read off — its state is
   * dictated by the parent row's other permissions, not by child sub-plugins.
   */
  const isPluginsReadLocked = (enabledIds) => {
    const pluginsFeature = normalizedAppFeatures["Plugins"];
    if (!pluginsFeature) return false;
    return (
      (pluginsFeature.create && enabledIds.includes(pluginsFeature.create)) ||
      (pluginsFeature.update && enabledIds.includes(pluginsFeature.update)) ||
      (pluginsFeature.delete && enabledIds.includes(pluginsFeature.delete))
    );
  };

  /**
   * Toggle a permission for a plugin sub-feature that comes from plugin_features API
   * (i.e. features_json.config). These are stored in formik's plugin_feature_permissions
   * field directly, using { plugin_feature_id, permission_json } format.
   *
   * For plugins without sub-features, this function also handles the initial migration
   * from enabled_app_feature_ids (how the API returns data) to plugin_feature_permissions
   * (how we track individual plugin permissions in the UI). On first toggle of any simple
   * plugin, all simple plugins are migrated so their state is tracked individually.
   *
   * @param {number} pluginFeatureId - The plugin_features[0].id
   * @param {string} featureKey - The sub-feature key (e.g. "dashboard")
   * @param {string} action - The permission action (e.g. "read", "create", "update", "delete")
   */
  const handlePluginPermissionToggle = (pluginFeatureId, featureKey, action) => {
    // Only migrate plugins that have a plugin_features record (pluginFeatureIdMap entry).
    // Plugins without a plugin_features record are always tracked via the shared "Plugins"
    // read id and do not need individual entries in plugin_feature_permissions.
    const currentPerms = [...(formik.values.plugin_feature_permissions || [])];
    const simplePlugins = allPluginEntries.filter((p) => !p.hasSubFeatures && pluginFeatureIdMap[p.name]);
    const anySimpleMigrated = simplePlugins.some((p) =>
      currentPerms.some((perm) => perm.plugin_feature_id === pluginFeatureIdMap[p.name])
    );

    // Migration: seed all trackable simple plugins from the shared "Plugins" read state.
    if (!anySimpleMigrated && simplePlugins.length > 0) {
      const pluginsFeature = normalizedAppFeatures["Plugins"];
      const sharedReadEnabled = !!(pluginsFeature?.read && formik.values.enabled_app_feature_ids.includes(pluginsFeature.read));
      simplePlugins.forEach((p) => {
        const pfId = pluginFeatureIdMap[p.name];
        const alreadyExists = currentPerms.some((perm) => perm.plugin_feature_id === pfId);
        if (!alreadyExists) {
          currentPerms.push({
            plugin_feature_id: pfId,
            permission_json: { [p.name.toLowerCase()]: { read: sharedReadEnabled } },
          });
        }
      });
    }

    const idx = currentPerms.findIndex((p) => p.plugin_feature_id === pluginFeatureId);

    if (idx === -1) {
      // Create new entry — auto-enable "read" when enabling create/update/delete
      const newPerms = { [action]: true };
      if (action !== "read") {
        newPerms.read = true;
      }
      currentPerms.push({
        plugin_feature_id: pluginFeatureId,
        permission_json: { [featureKey]: newPerms },
      });
    } else {
      const entry = { ...currentPerms[idx] };
      const permJson = { ...entry.permission_json };
      const featurePerms = { ...(permJson[featureKey] || {}) };
      featurePerms[action] = !featurePerms[action];

      // Auto-enable "read" when any other permission is enabled
      if (action !== "read" && featurePerms[action] && !featurePerms.read) {
        featurePerms.read = true;
      }

      permJson[featureKey] = featurePerms;
      entry.permission_json = permJson;
      currentPerms[idx] = entry;
    }

    formik.setFieldValue("plugin_feature_permissions", currentPerms);

    // Child-to-parent cascade: sync parent "Plugins" read checkbox
    // based on whether any plugin now has any permission enabled.
    // Skip when Plugins.Read is locked by another parent permission (Create/Update/Delete):
    // in that case Read is auto-enforced by the parent, not derived from children.
    const pluginsFeature = normalizedAppFeatures["Plugins"];
    if (pluginsFeature?.read && !isPluginsReadLocked(formik.values.enabled_app_feature_ids)) {
      const anyPluginHasPerm = anyPluginHasPermission(currentPerms, formik.values.enabled_app_feature_ids);
      let newEnabledIds = [...formik.values.enabled_app_feature_ids];
      if (anyPluginHasPerm && !newEnabledIds.includes(pluginsFeature.read)) {
        newEnabledIds.push(pluginsFeature.read);
        formik.setFieldValue("enabled_app_feature_ids", newEnabledIds);
      } else if (!anyPluginHasPerm && newEnabledIds.includes(pluginsFeature.read)) {
        formik.setFieldValue(
          "enabled_app_feature_ids",
          newEnabledIds.filter((id) => id !== pluginsFeature.read)
        );
      }
    }
  };

  /**
   * Returns true if any plugin has any permission enabled, given the current state.
   * Used for child-to-parent cascade.
   *
   * There are three categories of plugins:
   *   A. Plugin with app_features sub-features (pluginGroups) → tracked in enabled_app_feature_ids
   *   B. Plugin with pluginFeatConfig sub-features → tracked in plugin_feature_permissions
   *   C. Plugin without sub-features AND with plugin_features record → tracked in plugin_feature_permissions
   *      (migrated from enabled_app_feature_ids on first toggle)
   *   D. Plugin without sub-features AND without plugin_features record → always tracked via
   *      the shared "Plugins" read id in enabled_app_feature_ids (no individual tracking possible)
   */
  const anyPluginHasPermission = (pluginPerms, enabledIds) => {
    const pluginsFeature = normalizedAppFeatures["Plugins"];

    // Category D: plugins with no plugin_features record — check shared Plugins read
    const hasCategoryD = allPluginEntries.some(
      (p) => !p.hasSubFeatures && !pluginFeatureIdMap[p.name]
    );
    if (hasCategoryD && pluginsFeature?.read && enabledIds.includes(pluginsFeature.read)) {
      return true;
    }

    return allPluginEntries.some((plugin) => {
      // Category A: plugin with app_features sub-features
      if (plugin.subFeatures) {
        return Object.keys(plugin.subFeatures).some((featureKey) => {
          const feat = plugin.subFeatures[featureKey];
          return feat && ["read", "create", "update", "delete"].some((a) => feat[a] && enabledIds.includes(feat[a]));
        });
      }
      // Category B: plugin with pluginFeatConfig sub-features
      if (plugin.pluginFeatConfig) {
        const pfId = plugin.plugin_features?.[0]?.id;
        if (pfId) {
          const entry = pluginPerms.find((p) => p.plugin_feature_id === pfId);
          return entry && Object.values(entry.permission_json || {}).some((p) => Object.values(p).some(Boolean));
        }
        return false;
      }
      // Category C: plugin without sub-features but with a plugin_features record
      if (!plugin.hasSubFeatures && pluginFeatureIdMap[plugin.name]) {
        const pfId = pluginFeatureIdMap[plugin.name];
        const entry = pluginPerms.find((p) => p.plugin_feature_id === pfId);
        if (entry) {
          return Object.values(entry.permission_json || {}).some((p) => Object.values(p).some(Boolean));
        }
        // Not yet migrated — fall back to shared Plugins read
        return !!(pluginsFeature?.read && enabledIds.includes(pluginsFeature.read));
      }
      return false;
    });
  };

  /**
   * Check if a specific plugin feature permission is enabled.
   */
  const isPluginPermissionChecked = (pluginFeatureId, featureKey, action) => {
    const perms = formik.values.plugin_feature_permissions || [];
    const entry = perms.find((p) => p.plugin_feature_id === pluginFeatureId);
    return !!entry?.permission_json?.[featureKey]?.[action];
  };

  /**
   * Check if a plugin without sub-features has its read permission enabled.
   *
   * - If the plugin has a plugin_features record (pluginFeatureIdMap entry exists):
   *     Check plugin_feature_permissions. If no entry yet (not migrated), fall back to
   *     the shared "Plugins" read id in enabled_app_feature_ids.
   * - If the plugin has NO plugin_features record:
   *     Always use the shared "Plugins" read id — no individual tracking is possible.
   */
  const isSimplePluginReadChecked = (pluginFeatureId, pluginName) => {
    const pluginsFeature = normalizedAppFeatures["Plugins"];
    const sharedReadEnabled = !!(
      pluginsFeature?.read && formik.values.enabled_app_feature_ids.includes(pluginsFeature.read)
    );

    // No plugin_features record — always use shared Plugins read
    if (!pluginFeatureId) {
      return sharedReadEnabled;
    }

    const perms = formik.values.plugin_feature_permissions || [];
    const entry = perms.find((p) => p.plugin_feature_id === pluginFeatureId);
    // Explicit entry exists — use it as source of truth
    if (entry) {
      return !!entry.permission_json?.[pluginName.toLowerCase()]?.read;
    }
    // Not yet migrated — fall back to shared Plugins read
    return sharedReadEnabled;
  };

  /**
   * Check if ANY sub-feature of a plugin has "read" permission checked.
   * Used to determine the checked state of the plugin header read checkbox.
   */
  const isAnyPluginSubFeatureReadChecked = (pluginFeatureId, featConfig) => {
    if (!pluginFeatureId || !Array.isArray(featConfig)) return false;
    return featConfig.some((feat) => isPluginPermissionChecked(pluginFeatureId, feat.key, "read"));
  };

  /**
   * Check if the plugin header read checkbox should be disabled.
   * Disabled when any sub-feature has a non-read permission active (create/update/delete),
   * because that sub-feature's read is auto-locked.
   */
  const isPluginHeaderReadDisabled = (pluginFeatureId, featConfig) => {
    if (!pluginFeatureId || !Array.isArray(featConfig)) return false;
    return featConfig.some((feat) => {
      const allowedPerms = feat.allowed_permissions || ["read"];
      return allowedPerms.some(
        (a) => a !== "read" && isPluginPermissionChecked(pluginFeatureId, feat.key, a)
      );
    });
  };

  /**
   * Toggle "read" on ALL sub-features of a plugin at once.
   * If any sub-feature has read checked → uncheck all (except those locked by other perms).
   * If none has read checked → check all.
   */
  const handlePluginHeaderReadToggle = (pluginFeatureId, featConfig) => {
    if (!pluginFeatureId || !Array.isArray(featConfig)) return;

    const anyChecked = isAnyPluginSubFeatureReadChecked(pluginFeatureId, featConfig);
    const currentPerms = [...(formik.values.plugin_feature_permissions || [])];
    const idx = currentPerms.findIndex((p) => p.plugin_feature_id === pluginFeatureId);

    let permJson = {};
    if (idx !== -1) {
      permJson = { ...currentPerms[idx].permission_json };
    }

    featConfig.forEach((feat) => {
      const featurePerms = { ...(permJson[feat.key] || {}) };
      if (anyChecked) {
        // Uncheck read — but only if no other permission is active for this feature
        const allowedPerms = feat.allowed_permissions || ["read"];
        const hasOtherPerm = allowedPerms.some(
          (a) => a !== "read" && featurePerms[a]
        );
        if (!hasOtherPerm) {
          featurePerms.read = false;
        }
      } else {
        // Check read on all sub-features
        featurePerms.read = true;
      }
      permJson[feat.key] = featurePerms;
    });

    if (idx === -1) {
      currentPerms.push({
        plugin_feature_id: pluginFeatureId,
        permission_json: permJson,
      });
    } else {
      currentPerms[idx] = {
        ...currentPerms[idx],
        permission_json: permJson,
      };
    }

    formik.setFieldValue("plugin_feature_permissions", currentPerms);

    // Child-to-parent cascade: sync parent "Plugins" read checkbox based on whether
    // any plugin still has any permission enabled after this bulk toggle.
    // Skip when Plugins.Read is locked by another parent permission (Create/Update/Delete).
    const pluginsFeature = normalizedAppFeatures["Plugins"];
    if (pluginsFeature?.read && !isPluginsReadLocked(formik.values.enabled_app_feature_ids)) {
      const anyPluginHasPerm = anyPluginHasPermission(currentPerms, formik.values.enabled_app_feature_ids);
      const enabledIds = [...formik.values.enabled_app_feature_ids];
      const hasParentRead = enabledIds.includes(pluginsFeature.read);
      if (anyPluginHasPerm && !hasParentRead) {
        formik.setFieldValue("enabled_app_feature_ids", [...enabledIds, pluginsFeature.read]);
      } else if (!anyPluginHasPerm && hasParentRead) {
        formik.setFieldValue(
          "enabled_app_feature_ids",
          enabledIds.filter((id) => id !== pluginsFeature.read)
        );
      }
    }
  };

  /**
   *
   * @param {int} id : App feature id to be included/removed
   * @param {string} featureName : Name of the feature being toggled
   * @param {boolean} cascadeToPlugins : If true, cascade to child plugins (for "Plugins" parent row)
   * This method will help to add or remove app features from role
   */
  const handleRoleSelection = (id, featureName, cascadeToPlugins = false) => {
    let enabledAppFeatures = [...formik.values.enabled_app_feature_ids];

    if (enabledAppFeatures.includes(id)) {
      enabledAppFeatures = enabledAppFeatures.filter((item) => item !== id);
    } else {
      enabledAppFeatures.push(id);

      // Auto-enable Read when Create/Update/Delete is enabled
      const readId = normalizedAppFeatures[featureName]?.read;
      if (readId && !enabledAppFeatures.includes(readId)) {
        enabledAppFeatures.push(readId);
      }
    }

    // Cascade to child plugins if this is the "Plugins" parent row.
    // IMPORTANT: accumulate all plugin_feature_permissions mutations into a single
    // local array and commit via setFieldValue exactly once at the end. Calling
    // setFieldValue inside the loop caused each iteration to snapshot the same
    // (stale) formik.values and overwrite previous iterations' changes — resulting
    // in only the last processed plugin being persisted.
    let pluginPermsChanged = false;
    let accumulatedPluginPerms = [...(formik.values.plugin_feature_permissions || [])];

    if (cascadeToPlugins && featureName === "Plugins") {
      const pluginsFeature = normalizedAppFeatures["Plugins"];
      if (pluginsFeature) {
        // Determine which action (read/create/update/delete) was toggled
        const toggledAction = ["read", "create", "update", "delete"].find(
          (a) => pluginsFeature[a] === id
        );
        const isChecking = !formik.values.enabled_app_feature_ids.includes(id);

        allPluginEntries.forEach((plugin) => {
          // Case 1: Plugin without sub-features - use individual plugin_feature_permissions.
          // Use the plugin's own name (lowercased) as the permission_json key so the payload
          // is meaningful and not hardcoded to the generic "plugin" string.
          if (!plugin.hasSubFeatures) {
            const pfId = pluginFeatureIdMap[plugin.name];
            const permKey = plugin.name.toLowerCase();
            if (pfId && toggledAction === "read") {
              const idx = accumulatedPluginPerms.findIndex((p) => p.plugin_feature_id === pfId);
              if (isChecking) {
                if (idx === -1) {
                  accumulatedPluginPerms.push({
                    plugin_feature_id: pfId,
                    permission_json: { [permKey]: { read: true } },
                  });
                } else {
                  accumulatedPluginPerms[idx] = {
                    ...accumulatedPluginPerms[idx],
                    permission_json: {
                      ...accumulatedPluginPerms[idx].permission_json,
                      [permKey]: {
                        ...(accumulatedPluginPerms[idx].permission_json?.[permKey] || {}),
                        read: true,
                      },
                    },
                  };
                }
              } else if (idx !== -1) {
                const newPermJson = { ...accumulatedPluginPerms[idx].permission_json };
                if (newPermJson[permKey]) {
                  newPermJson[permKey] = { ...newPermJson[permKey], read: false };
                }
                accumulatedPluginPerms[idx] = {
                  ...accumulatedPluginPerms[idx],
                  permission_json: newPermJson,
                };
              }
              pluginPermsChanged = true;
            }
          }
          // Case 2: Plugin with sub-features from app_features (pluginGroups)
          else if (plugin.subFeatures) {
            Object.keys(plugin.subFeatures).forEach((featureKey) => {
              // plugin.subFeatures[featureKey] is already the feature object { read, create, update, delete }
              const subFeature = plugin.subFeatures[featureKey];
              if (subFeature && toggledAction && subFeature[toggledAction]) {
                const subFeatureId = subFeature[toggledAction];
                if (isChecking) {
                  if (!enabledAppFeatures.includes(subFeatureId)) enabledAppFeatures.push(subFeatureId);
                } else {
                  enabledAppFeatures = enabledAppFeatures.filter((item) => item !== subFeatureId);
                }
              }
            });
          }
          // Case 3: Plugin with sub-features from plugin_features API (pluginFeatConfig)
          // Only "read" applies here since pluginFeatConfig only exposes read at the header level
          else if (plugin.pluginFeatConfig && toggledAction === "read") {
            const pfId = plugin.plugin_features?.[0]?.id;
            if (pfId) {
              const idx = accumulatedPluginPerms.findIndex((p) => p.plugin_feature_id === pfId);
              let permJson = idx !== -1 ? { ...accumulatedPluginPerms[idx].permission_json } : {};

              plugin.pluginFeatConfig.forEach((feat) => {
                permJson[feat.key] = { ...(permJson[feat.key] || {}), read: isChecking };
              });

              if (idx === -1) {
                accumulatedPluginPerms.push({ plugin_feature_id: pfId, permission_json: permJson });
              } else {
                accumulatedPluginPerms[idx] = {
                  ...accumulatedPluginPerms[idx],
                  permission_json: permJson,
                };
              }
              pluginPermsChanged = true;
            }
          }
        });
      }
    }

    // Commit plugin_feature_permissions mutations once (if any occurred).
    if (pluginPermsChanged) {
      formik.setFieldValue("plugin_feature_permissions", accumulatedPluginPerms);
    }

    formik.setFieldValue("enabled_app_feature_ids", enabledAppFeatures);

    // Child-to-parent cascade for app_features-based plugin sub-features:
    // if featureName contains PLUGIN_SEPARATOR, it's a plugin sub-feature — sync parent Plugins read.
    // Skip when Plugins.Read is locked by another parent permission (Create/Update/Delete).
    if (!cascadeToPlugins && featureName.includes(PLUGIN_SEPARATOR)) {
      const pluginsFeature = normalizedAppFeatures["Plugins"];
      if (pluginsFeature?.read && !isPluginsReadLocked(enabledAppFeatures)) {
        const anyHasPerm = anyPluginHasPermission(
          formik.values.plugin_feature_permissions || [],
          enabledAppFeatures
        );
        if (anyHasPerm && !enabledAppFeatures.includes(pluginsFeature.read)) {
          formik.setFieldValue("enabled_app_feature_ids", [...enabledAppFeatures, pluginsFeature.read]);
        } else if (!anyHasPerm) {
          formik.setFieldValue(
            "enabled_app_feature_ids",
            enabledAppFeatures.filter((id) => id !== pluginsFeature.read)
          );
        }
      }
    }
  };

  /**
   * Check if Read checkbox should be disabled for a feature
   * Read is disabled if any of Create/Update/Delete is enabled
   */
  const isReadDisabled = (featureName) => {
    const feature = normalizedAppFeatures[featureName];
    const enabledIds = formik.values.enabled_app_feature_ids;

    return (
      (feature.create && enabledIds.includes(feature.create)) ||
      (feature.update && enabledIds.includes(feature.update)) ||
      (feature.delete && enabledIds.includes(feature.delete))
    );
  };

  const handleFormSubmit = (formData) => {
    // Separate regular app feature IDs from plugin sub-feature IDs
    const regularFeatureIds = formData.enabled_app_feature_ids.filter(
      (id) => !pluginSubFeatureIds.has(id)
    );

    // Build plugin_feature_permissions from two sources:
    // 1. Plugin sub-features from app_features (pluginGroups) — derive from enabled_app_feature_ids
    // 2. Plugin sub-features from plugin_features API (features_json.config) — from formik state
    const pluginFeaturePermissions = [];

    // Source 1: app_features-based plugin sub-features
    Object.keys(pluginGroups).forEach((pluginName) => {
      const pfId = pluginFeatureIdMap[pluginName];
      if (!pfId) return;

      const features = pluginGroups[pluginName];
      const permissionJson = {};

      Object.keys(features).forEach((featureKey) => {
        const fullName = `${pluginName}${PLUGIN_SEPARATOR}${featureKey}`;
        const feat = normalizedAppFeatures[fullName];
        if (!feat) return;

        permissionJson[featureKey] = {};
        ["read", "create", "update", "delete"].forEach((action) => {
          permissionJson[featureKey][action] = feat[action]
            ? formData.enabled_app_feature_ids.includes(feat[action])
            : false;
        });
      });

      const hasAnyPermission = Object.values(permissionJson).some((perms) =>
        Object.values(perms).some(Boolean)
      );

      if (hasAnyPermission) {
        pluginFeaturePermissions.push({
          plugin_feature_id: pfId,
          permission_json: permissionJson,
        });
      }
    });

    // Source 2: plugin_features API-based sub-features (from formik state)
    // These are for plugins that have features_json.config but no app_features entries
    if (Array.isArray(formData.plugin_feature_permissions)) {
      formData.plugin_feature_permissions.forEach((perm) => {
        // Skip if already handled by source 1
        const alreadyHandled = pluginFeaturePermissions.some(
          (p) => p.plugin_feature_id === perm.plugin_feature_id
        );
        if (alreadyHandled) return;

        // Only include if at least one permission is true
        const hasAnyPermission = Object.values(perm.permission_json || {}).some((perms) =>
          Object.values(perms).some(Boolean)
        );

        if (hasAnyPermission) {
          pluginFeaturePermissions.push(perm);
        }
      });
    }

    const payload = {
      name: formData.name,
      app_id: formData.app_id,
      enabled_app_feature_ids: regularFeatureIds,
      plugin_feature_permissions: pluginFeaturePermissions,
    };

    if (idToEdit) {
      update({ formData: payload, id: idToEdit });
    } else {
      create({ formData: payload });
    }
  };

  /**
   *
   * @param {String} action : one of available actions in ["create","update","delete"]
   * This methods helps to detect whether actions of one type is selected or not
   * @returns {boolean}
   */
  const isAllActionsSelected = (action) => {
    for (let i = 0; i < actionIds[action]?.length; i++) {
      if (!formik.values.enabled_app_feature_ids.includes(actionIds[action][i])) {
        return false;
      }
    }
    return true;
  };

  const handleSelectAll = (action) => {
    if (isAllActionsSelected(action)) {
      let tempSelectedAction = formik.values.enabled_app_feature_ids.filter(
        (id) => !actionIds[action].includes(id)
      );

      // If unchecking Read, keep Read permissions that are required by Create/Update/Delete
      if (action === 'read') {
        Object.keys(normalizedAppFeatures).forEach((featureName) => {
          const feature = normalizedAppFeatures[featureName];
          const readId = feature.read;

          // Check if this feature has any Create/Update/Delete enabled
          const hasOtherPermissions =
            (feature.create && tempSelectedAction.includes(feature.create)) ||
            (feature.update && tempSelectedAction.includes(feature.update)) ||
            (feature.delete && tempSelectedAction.includes(feature.delete));

          // If other permissions exist, keep the Read permission
          if (hasOtherPermissions && readId && !tempSelectedAction.includes(readId)) {
            tempSelectedAction.push(readId);
          }
        });
      }

      formik.setFieldValue("enabled_app_feature_ids", tempSelectedAction);
    } else {
      let tempSelectedAction = [...formik.values.enabled_app_feature_ids];

      actionIds[action].forEach((actionId) => {
        if (!tempSelectedAction.includes(actionId)) {
          tempSelectedAction.push(actionId);
        }
      });

      // Auto-enable all Read permissions when selecting all Create/Update/Delete
      if (action === 'create' || action === 'update' || action === 'delete') {
        actionIds.read.forEach((readId) => {
          if (!tempSelectedAction.includes(readId)) {
            tempSelectedAction.push(readId);
          }
        });
      }

      formik.setFieldValue("enabled_app_feature_ids", tempSelectedAction);
    }
  };

  /**
   *
   * This Effect will group the action ids by action type
   *
   */
  useEffect(() => {
    if (normalizedAppFeatures) {
      const tempActionIds = { create: [], update: [], delete: [], read: [] };

      Object.keys(normalizedAppFeatures).forEach((key) => {
        if (normalizedAppFeatures[key].create) {
          tempActionIds.create.push(normalizedAppFeatures[key].create);
        }
        if (normalizedAppFeatures[key].update) {
          tempActionIds.update.push(normalizedAppFeatures[key].update);
        }
        if (normalizedAppFeatures[key].delete) {
          tempActionIds.delete.push(normalizedAppFeatures[key].delete);
        }
        if (normalizedAppFeatures[key].read) {
          tempActionIds.read.push(normalizedAppFeatures[key].read);
        }
      });
      setActionIds(tempActionIds);
    }
  }, [normalizedAppFeatures]);

  // Set form data in case of edit
  useEffect(() => {
    if (idToEdit && data) {
      const merged = mergeObjects(formik.values, data);
      // API getRole may not return app_id; default to the selected app id from
      // localStorage (falling back to "1") so validation passes.
      if (!merged.app_id) {
        merged.app_id = getSelectedAppId();
      }
      // Explicitly set plugin_feature_permissions from API response.
      // mergeObjects uses `||` which would keep the empty initial array when
      // the API returns an empty array (falsy). Ensure we always take the API value.
      merged.plugin_feature_permissions = Array.isArray(data.plugin_feature_permissions)
        ? data.plugin_feature_permissions
        : [];
      setInitialValues(merged);
    }
  }, [data]);

  return (
    <FormContainer
      resourceName={getLocalizedString("role", "Role")}
      closeForm={() => onClose(false)}
      handleFormSubmit={formik.handleSubmit}
      idToEdit={idToEdit}
    >
      <div className="w-50">
        <Components.QIInput
          label={getLocalizedString("name", "Name")}
          {...formik.getFieldProps("name")}
          error={formik.touched.name && formik.errors.name}
        />
      </div>
      <div className="text-center">
        <small className="text-danger">{formik.errors.enabled_app_feature_ids}</small>
      </div>
      {normalizedAppFeatures && Object.keys(normalizedAppFeatures).length > 0 ? (
        <div className="roles-form-list">
          <div className="qi-list-view ">
            <div className="qi-list-view_header">
              <div className="qi-list-view_column feature-name-column"></div>
              {[
                {
                  label: getLocalizedString("read", "Read"),
                  key: "read",
                  className: "read-column read-column",
                },
                {
                  label: getLocalizedString("create", "Create"),
                  key: "create",
                  className: "create-column create-column",
                },
                {
                  label: getLocalizedString("update", "Update"),
                  key: "update",
                  className: "update-column update-column",
                },
                {
                  label: getLocalizedString("delete", "Delete"),
                  key: "delete",
                  className: "delete-column delete-column",
                },
              ].map((header, index) => (
                <div className={`qi-list-view_column ${header.className}`} key={index}>
                  <input
                    type="checkbox"
                    checked={
                      formik?.values?.name == "Admin" ? "true" : isAllActionsSelected(header.key)
                    }
                    disabled={formik?.values?.name == "Admin" && true}
                    onChange={() => handleSelectAll(header.key)}
                  />
                  {header.label}
                </div>
              ))}
            </div>
            <ul className="qi-list-view_list">
              {/* Regular features — including "Plugins" row */}
              {Object.keys(regularFeatures).map((key, index) => (
                <React.Fragment key={`regular-${index}`}>
                  <li className="qi-list-view_list_item">
                    <div className="qi-list-view_column feature-name-column" title={key}>
                      {key}
                    </div>
                    <div className="qi-list-view_column read-column">
                      {normalizedAppFeatures[key].read && (
                        <input
                          type="checkbox"
                          onChange={() =>
                            handleRoleSelection(normalizedAppFeatures[key].read, key, key === "Plugins")
                          }
                          checked={
                            formik?.values?.name === "Admin"
                              ? true
                              : formik.values.enabled_app_feature_ids.includes(
                                  normalizedAppFeatures[key].read
                                )
                          }
                          disabled={formik?.values?.name === "Admin" || isReadDisabled(key)}
                          readOnly={formik?.values?.name === "Admin"}
                        />
                      )}
                    </div>
                    <div className="qi-list-view_column create-column">
                      {normalizedAppFeatures[key].create && (
                        <input
                          type="checkbox"
                          onChange={() =>
                            handleRoleSelection(
                              normalizedAppFeatures[key].create,
                              key,
                              key === "Plugins"
                            )
                          }
                          checked={
                            formik?.values?.name === "Admin"
                              ? true
                              : formik.values.enabled_app_feature_ids.includes(
                                  normalizedAppFeatures[key].create
                                )
                          }
                          disabled={formik?.values?.name === "Admin"}
                          readOnly={formik?.values?.name === "Admin"}
                        />
                      )}
                    </div>
                    <div className="qi-list-view_column update-column">
                      {normalizedAppFeatures[key].update && (
                        <input
                          type="checkbox"
                          onChange={() =>
                            handleRoleSelection(
                              normalizedAppFeatures[key].update,
                              key,
                              key === "Plugins"
                            )
                          }
                          checked={
                            formik?.values?.name === "Admin"
                              ? true
                              : formik.values.enabled_app_feature_ids.includes(
                                  normalizedAppFeatures[key].update
                                )
                          }
                          disabled={formik?.values?.name === "Admin"}
                          readOnly={formik?.values?.name === "Admin"}
                        />
                      )}
                    </div>
                    <div className="qi-list-view_column delete-column">
                      {normalizedAppFeatures[key].delete && (
                        <input
                          type="checkbox"
                          onChange={() =>
                            handleRoleSelection(
                              normalizedAppFeatures[key].delete,
                              key,
                              key === "Plugins"
                            )
                          }
                          checked={
                            formik?.values?.name === "Admin"
                              ? true
                              : formik.values.enabled_app_feature_ids.includes(
                                  normalizedAppFeatures[key].delete
                                )
                          }
                          disabled={formik?.values?.name === "Admin"}
                          readOnly={formik?.values?.name === "Admin"}
                        />
                      )}
                    </div>
                  </li>

                  {/* After "Plugins" row, render individual plugin entries indented */}
                  {key === "Plugins" &&
                    allPluginEntries.map((plugin) => {
                      const isExpanded = expandedPlugins[plugin.name];
                      const pluginsFeature = normalizedAppFeatures["Plugins"];

                      return (
                        <React.Fragment key={`plugin-entry-${plugin.id}`}>
                          {/* Plugin name row — accordion header, indented */}
                          <li
                            className="qi-list-view_list_item plugin-group-header"
                            style={{
                              paddingLeft: "20px",
                              cursor: plugin.hasSubFeatures ? "pointer" : "default",
                              fontWeight: "bold",
                            }}
                            onClick={() =>
                              plugin.hasSubFeatures && togglePluginGroup(plugin.name)
                            }
                          >
                            <div
                              className="qi-list-view_column feature-name-column"
                              title={plugin.name}
                            >
                              {plugin.hasSubFeatures && (
                                <span style={{ marginRight: "6px" }}>
                                  {isExpanded ? "▼" : "▶"}
                                </span>
                              )}
                              {plugin.name}
                            </div>
                            {/* Plugins without sub-features: individual read checkbox via plugin_feature_permissions */}
                            {!plugin.hasSubFeatures ? (
                              <>
                                <div className="qi-list-view_column read-column">
                                  <input
                                    type="checkbox"
                                    onChange={() => handlePluginPermissionToggle(pluginFeatureIdMap[plugin.name], plugin.name.toLowerCase(), "read")}
                                    checked={
                                      formik?.values?.name === "Admin"
                                        ? true
                                        : isSimplePluginReadChecked(pluginFeatureIdMap[plugin.name], plugin.name)
                                    }
                                    disabled={formik?.values?.name === "Admin"}
                                    readOnly={formik?.values?.name === "Admin"}
                                  />
                                </div>
                                <div className="qi-list-view_column create-column" />
                                <div className="qi-list-view_column update-column" />
                                <div className="qi-list-view_column delete-column" />
                              </>
                            ) : plugin.pluginFeatConfig ? (
                              <>
                                <div
                                  className="qi-list-view_column read-column"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <input
                                    type="checkbox"
                                    onChange={() =>
                                      handlePluginHeaderReadToggle(
                                        plugin.plugin_features?.[0]?.id,
                                        plugin.pluginFeatConfig
                                      )
                                    }
                                    checked={
                                      formik?.values?.name === "Admin"
                                        ? true
                                        : isAnyPluginSubFeatureReadChecked(
                                            plugin.plugin_features?.[0]?.id,
                                            plugin.pluginFeatConfig
                                          )
                                    }
                                    disabled={
                                      formik?.values?.name === "Admin" ||
                                      isPluginHeaderReadDisabled(
                                        plugin.plugin_features?.[0]?.id,
                                        plugin.pluginFeatConfig
                                      )
                                    }
                                    readOnly={formik?.values?.name === "Admin"}
                                  />
                                </div>
                                <div className="qi-list-view_column create-column" />
                                <div className="qi-list-view_column update-column" />
                                <div className="qi-list-view_column delete-column" />
                              </>
                            ) : (
                              <>
                                <div className="qi-list-view_column read-column" />
                                <div className="qi-list-view_column create-column" />
                                <div className="qi-list-view_column update-column" />
                                <div className="qi-list-view_column delete-column" />
                              </>
                            )}
                          </li>

                          {/* Sub-features when expanded — double indented */}
                          {/* Source 1: from app_features (pluginGroups) — uses enabled_app_feature_ids */}
                          {plugin.subFeatures &&
                            isExpanded &&
                            Object.keys(plugin.subFeatures).map((featureKey, subIndex) => {
                              const fullFeatureName = `${plugin.name}${PLUGIN_SEPARATOR}${featureKey}`;
                              const feature = normalizedAppFeatures[fullFeatureName];

                              return (
                                <li
                                  className="qi-list-view_list_item plugin-sub-feature"
                                  key={`plugin-${plugin.name}-sub-${subIndex}`}
                                  style={{ paddingLeft: "40px" }}
                                >
                                  <div
                                    className="qi-list-view_column feature-name-column"
                                    title={featureKey}
                                  >
                                    {featureKey}
                                  </div>
                                  <div className="qi-list-view_column read-column">
                                    {feature?.read && (
                                      <input
                                        type="checkbox"
                                        onChange={() =>
                                          handleRoleSelection(feature.read, fullFeatureName)
                                        }
                                        checked={
                                          formik?.values?.name === "Admin"
                                            ? true
                                            : formik.values.enabled_app_feature_ids.includes(
                                                feature.read
                                              )
                                        }
                                        disabled={
                                          formik?.values?.name === "Admin" ||
                                          isReadDisabled(fullFeatureName)
                                        }
                                        readOnly={formik?.values?.name === "Admin"}
                                      />
                                    )}
                                  </div>
                                  <div className="qi-list-view_column create-column">
                                    {feature?.create && (
                                      <input
                                        type="checkbox"
                                        onChange={() =>
                                          handleRoleSelection(feature.create, fullFeatureName)
                                        }
                                        checked={
                                          formik?.values?.name === "Admin"
                                            ? true
                                            : formik.values.enabled_app_feature_ids.includes(
                                                feature.create
                                              )
                                        }
                                        disabled={formik?.values?.name === "Admin"}
                                        readOnly={formik?.values?.name === "Admin"}
                                      />
                                    )}
                                  </div>
                                  <div className="qi-list-view_column update-column">
                                    {feature?.update && (
                                      <input
                                        type="checkbox"
                                        onChange={() =>
                                          handleRoleSelection(feature.update, fullFeatureName)
                                        }
                                        checked={
                                          formik?.values?.name === "Admin"
                                            ? true
                                            : formik.values.enabled_app_feature_ids.includes(
                                                feature.update
                                              )
                                        }
                                        disabled={formik?.values?.name === "Admin"}
                                        readOnly={formik?.values?.name === "Admin"}
                                      />
                                    )}
                                  </div>
                                  <div className="qi-list-view_column delete-column">
                                    {feature?.delete && (
                                      <input
                                        type="checkbox"
                                        onChange={() =>
                                          handleRoleSelection(feature.delete, fullFeatureName)
                                        }
                                        checked={
                                          formik?.values?.name === "Admin"
                                            ? true
                                            : formik.values.enabled_app_feature_ids.includes(
                                                feature.delete
                                              )
                                        }
                                        disabled={formik?.values?.name === "Admin"}
                                        readOnly={formik?.values?.name === "Admin"}
                                      />
                                    )}
                                  </div>
                                </li>
                              );
                            })}

                          {/* Source 2: from plugin_features API (features_json.config) */}
                          {/* Renders checkboxes based on each feature's allowed_permissions */}
                          {plugin.pluginFeatConfig &&
                            isExpanded &&
                            plugin.pluginFeatConfig.map((feat, subIndex) => {
                              const pfId = plugin.plugin_features?.[0]?.id;
                              if (!pfId) return null;

                              const allowedPerms = feat.allowed_permissions || ["read"];

                              return (
                                <li
                                  className="qi-list-view_list_item plugin-sub-feature"
                                  key={`plugin-${plugin.name}-pf-${subIndex}`}
                                  style={{ paddingLeft: "40px" }}
                                >
                                  <div
                                    className="qi-list-view_column feature-name-column"
                                    title={feat.label}
                                  >
                                    {feat.label}
                                  </div>
                                  {["read", "create", "update", "delete"].map((action) => {
                                    const columnClass =
                                      action === "read"
                                        ? "read-column"
                                        : action === "create"
                                        ? "create-column"
                                        : action === "update"
                                        ? "update-column"
                                        : "delete-column";

                                    if (!allowedPerms.includes(action)) {
                                      return (
                                        <div
                                          className={`qi-list-view_column ${columnClass}`}
                                          key={action}
                                        />
                                      );
                                    }

                                    const isRead = action === "read";
                                    // Disable read if any other allowed permission is checked
                                    const readLocked =
                                      isRead &&
                                      allowedPerms.some(
                                        (a) =>
                                          a !== "read" &&
                                          isPluginPermissionChecked(pfId, feat.key, a)
                                      );

                                    return (
                                      <div
                                        className={`qi-list-view_column ${columnClass}`}
                                        key={action}
                                      >
                                        <input
                                          type="checkbox"
                                          onChange={() =>
                                            handlePluginPermissionToggle(pfId, feat.key, action)
                                          }
                                          checked={
                                            formik?.values?.name === "Admin"
                                              ? true
                                              : isPluginPermissionChecked(pfId, feat.key, action)
                                          }
                                          disabled={
                                            formik?.values?.name === "Admin" || readLocked
                                          }
                                          readOnly={formik?.values?.name === "Admin"}
                                        />
                                      </div>
                                    );
                                  })}
                                </li>
                              );
                            })}
                        </React.Fragment>
                      );
                    })}
                </React.Fragment>
              ))}
            </ul>
          </div>
        </div>
      ) : (
        formik.values.app_id == 2 && (
          <div className="text-center text-red-600">
            {(!isAppFeaturesLoading &&
              formik.values.app_id &&
              getLocalizedString("no_features_are_associated_with_given_application", "")) ||
              "No features are associated with given application"}
          </div>
        )
      )}
    </FormContainer>
  );
};
